export interface GeoPoint {
  lat: number;
  lng: number;
}

export class GeoDistanceService {
  private static readonly EARTH_RADIUS_METERS = 6371000;

  private static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  static distanceInMeters(a: GeoPoint, b: GeoPoint): number {
    const dLat = this.toRadians(b.lat - a.lat);
    const dLng = this.toRadians(b.lng - a.lng);

    const lat1 = this.toRadians(a.lat);
    const lat2 = this.toRadians(b.lat);

    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);

    const h =
      sinDLat * sinDLat +
      Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;

    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));

    return this.EARTH_RADIUS_METERS * c;
  }

  static sortByDistance<T extends GeoPoint>(
    origin: GeoPoint,
    points: T[]
  ): Array<T & { meters: number }> {
    return points
      .map(p => ({
        ...p,
        meters: this.distanceInMeters(origin, p),
      }))
      .sort((a, b) => a.meters - b.meters);
  }

  static nearest<T extends GeoPoint>(
    origin: GeoPoint,
    points: T[]
  ): (T & { meters: number }) | null {
    if (!points.length) return null;
    return this.sortByDistance(origin, points)[0];
  }
}
